import type { FC } from 'react'
import { useCallback, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material'
import type { StorageMode } from '@/infra/crypto/key-ring'

interface StorageModeCheckboxProps {
  readonly currentMode: StorageMode
  readonly onModeChange: (mode: StorageMode) => void
  readonly disabled?: boolean
}

export const StorageModeCheckbox: FC<StorageModeCheckboxProps> = ({
  currentMode,
  onModeChange,
  disabled = false,
}) => {
  const [pendingTransition, setPendingTransition] = useState<StorageMode | null>(null)

  const isMemoryMode = currentMode === 'memory'

  const handleCheckboxChange = useCallback(() => {
    const targetMode: StorageMode = isMemoryMode ? 'localStorage' : 'memory'
    setPendingTransition(targetMode)
  }, [isMemoryMode])

  const handleConfirm = useCallback(() => {
    if (pendingTransition) {
      onModeChange(pendingTransition)
    }
    setPendingTransition(null)
  }, [pendingTransition, onModeChange])

  const handleCancel = useCallback(() => {
    setPendingTransition(null)
  }, [])

  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={isMemoryMode}
            onChange={handleCheckboxChange}
            disabled={disabled}
            size="small"
          />
        }
        label={
          <Box>
            <Typography variant="body2" sx={labelTextSx}>
              Require key import on each session
            </Typography>
            <Typography variant="caption" sx={helperTextSx}>
              {isMemoryMode
                ? 'Keys are held in memory only and cleared on page refresh.'
                : 'Keys persist in browser storage across sessions.'}
            </Typography>
          </Box>
        }
        sx={formControlSx}
      />

      <Dialog
        open={pendingTransition === 'localStorage'}
        onClose={handleCancel}
        aria-labelledby="persist-keys-dialog-title"
        aria-describedby="persist-keys-dialog-description"
      >
        <DialogTitle id="persist-keys-dialog-title">Persist Keys in Browser?</DialogTitle>
        <DialogContent>
          <DialogContentText id="persist-keys-dialog-description">
            Storing encryption keys in browser storage is convenient but less secure. Keys will
            remain accessible to anyone with access to this browser. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleConfirm} color="warning" variant="contained">
            Persist Keys
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pendingTransition === 'memory'}
        onClose={handleCancel}
        aria-labelledby="memory-mode-dialog-title"
        aria-describedby="memory-mode-dialog-description"
      >
        <DialogTitle id="memory-mode-dialog-title">Switch to Memory-Only Mode?</DialogTitle>
        <DialogContent>
          <DialogContentText id="memory-mode-dialog-description">
            Keys will be cleared from browser storage and you&apos;ll need to import your key ring
            file on each login.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained">
            Switch to Memory
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const formControlSx: SxProps<Theme> = {
  alignItems: 'flex-start',
  ml: 0,
  '& .MuiCheckbox-root': {
    pt: 0.25,
  },
}

const labelTextSx: SxProps<Theme> = {
  color: 'text.primary',
  fontSize: '0.85rem',
}

const helperTextSx: SxProps<Theme> = {
  color: 'text.disabled',
  fontSize: '0.75rem',
  display: 'block',
  lineHeight: 1.4,
}
